// @ts-nocheck
import React, { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  ArrowRight,
  Plus,
  Printer,
  Loader2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Layers,
  Search,
  Filter,
  Eye,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { toast } from "sonner";
import { trpc, trpcClient } from "@/lib/trpc";
import { cn, formatNumber } from "@/lib/utils";
import { CompactKpiRibbon } from "@/components/shared/CompactKpiRibbon";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import PrintDialog from "@/components/shared/PrintDialog";
import { NumericInput } from "@/components/ui/numeric-input";
import { ReceiveWipModal } from "./components/ReceiveWipModal";
import WipTransferPrintTemplate from "@/components/factory/print/WipTransferPrintTemplate";
import { WipTransferDetailDrawer } from "./components/WipTransferDetailDrawer";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
  type InlineFilterDefinition,
} from "@/components/shared/InlineFilterRow";
import { useExcelExport } from "@/hooks/useExcelExport";
import { downloadExcelTemplate, type ExcelColumnDef } from "@/lib/excel.utils";
import { emsTabClass } from "@/components/ui/ems-tabs";

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "transferId", label: "Mã Phiếu Bàn Giao", width: 18 },
  { key: "palletCode", label: "Mã Pallet / Thẻ Kho", width: 18 },
  { key: "productionOrderId", label: "Mã Lệnh SX", width: 18 },
  { key: "productName", label: "Sản Phẩm", width: 25 },
  { key: "fromStep", label: "Công Đoạn Giao", width: 16 },
  { key: "toStep", label: "Công Đoạn Nhận", width: 16 },
  { key: "quantity", label: "SL Bàn Giao", width: 14, format: "number" },
  { key: "receivedQty", label: "SL Thực Nhận", width: 14, format: "number" },
  { key: "rejectedQty", label: "SL Lỗi / Bù Hao", width: 14, format: "number" },
  { key: "status", label: "Trạng Thái", width: 16 },
  { key: "senderName", label: "Người Giao", width: 18 },
  { key: "receiverName", label: "Người Nhận", width: 18 },
  { key: "createdAt", label: "Thời Gian Giao", width: 18 },
];

const FILTER_FIELDS: InlineFilterDefinition[] = [
  { key: "checkbox", type: "none" },
  { key: "transferId", placeholder: "Mã phiếu...", type: "text" },
  { key: "productionOrderId", placeholder: "Mã LSX...", type: "text" },
  { key: "productName", placeholder: "Sản phẩm...", type: "text" },
  { key: "steps", placeholder: "Công đoạn...", type: "text" },
  { key: "quantity", type: "none" },
  { key: "receivedQty", type: "none" },
  {
    key: "status",
    placeholder: "Tất cả",
    type: "select",
    options: [
      { value: "PENDING", label: "Đang chuyển" },
      { value: "RECEIVED", label: "Đã nhận" },
      { value: "REJECTED", label: "Từ chối" },
    ],
  },
  { key: "user", placeholder: "Người giao / nhận...", type: "text" },
  { key: "actions", type: "none" },
];

const createTransferSchema = z.object({
  productionOrderId: z.string().min(1, "Vui lòng chọn Lệnh sản xuất"),
  fromStep: z.string().min(1, "Vui lòng chọn công đoạn giao"),
  toStep: z.string().min(1, "Vui lòng chọn công đoạn nhận"),
  fromRoutingStepId: z.string().optional(),
  toRoutingStepId: z.string().optional(),
  quantity: z.number().int().min(1, "Số lượng phải lớn hơn 0"),
  palletCode: z.string().optional(),
  senderName: z.string().optional(),
  notes: z.string().optional(),
});

type CreateTransferFormValues = z.infer<typeof createTransferSchema>;

export default function WipTransferList() {
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [printData, setPrintData] = useState<any[]>([]);
  const [receiveModalState, setReceiveModalState] = useState<{ open: boolean; transfer: any | null }>({
    open: false,
    transfer: null,
  });
  const [selectedTransfer, setSelectedTransfer] = useState<any | null>(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);

  // 1. Fetch active production orders for selection
  const { data: lsxData } = useQuery({
    queryKey: ["active-production-orders-wip"],
    queryFn: () =>
      trpcClient.productionOrders.list.query({
        limit: 100,
        status: "IN_PROGRESS",
      }),
  });

  const lsxOptions = useMemo(() => {
    return (
      lsxData?.items?.map((lsx: any) => ({
        id: lsx.id,
        label: `${lsx.orderId} - ${lsx.productName}`,
      })) || []
    );
  }, [lsxData]);

  // Form setup
  const form = useForm<CreateTransferFormValues>({
    resolver: zodResolver(createTransferSchema),
    defaultValues: {
      productionOrderId: "",
      fromStep: "",
      toStep: "",
      quantity: 0,
      palletCode: "",
      senderName: "",
      notes: "",
    },
  });

  const watchedProductionOrderId = form.watch("productionOrderId");

  // Fetch WIP balance for selected PO
  const { data: poBalanceData } = useQuery({
    queryKey: ["wip-balance-po", watchedProductionOrderId],
    queryFn: () => trpcClient.wipTransfer.getWipBalanceByPO.query({ productionOrderId: watchedProductionOrderId }),
    enabled: !!watchedProductionOrderId,
  });

  const availableSteps = useMemo(() => {
    return poBalanceData?.steps || [];
  }, [poBalanceData]);

  const fromStepOptions = useMemo(() => {
    return availableSteps.map((s: any) => ({
      id: s.stepCode || s.stepId,
      routingStepId: s.stepId,
      name: s.name,
      holdingQty: s.holdingQty,
      label: `${s.name} (Tồn: ${formatNumber(s.holdingQty)} Tờ)`,
    }));
  }, [availableSteps]);

  const watchedFromStep = form.watch("fromStep");

  // Auto-suggest next step when fromStep changes
  const toStepOptions = useMemo(() => {
    if (!watchedFromStep || availableSteps.length === 0) return [];
    const currentIdx = availableSteps.findIndex((s: any) => s.stepCode === watchedFromStep || s.name === watchedFromStep);
    if (currentIdx === -1) return [];

    // Subsequent steps
    return availableSteps.slice(currentIdx + 1).map((s: any) => ({
      id: s.stepCode || s.stepId,
      routingStepId: s.stepId,
      name: s.name,
      label: s.name,
    }));
  }, [watchedFromStep, availableSteps]);

  // Update constraint when fromStep changes
  React.useEffect(() => {
    if (watchedFromStep && availableSteps.length > 0) {
      const selected = availableSteps.find((s: any) => s.stepCode === watchedFromStep || s.name === watchedFromStep);
      if (selected) {
        form.setValue("fromRoutingStepId", selected.stepId);
        form.setValue("fromStep", selected.name);

        // Auto-select the immediate next step if available
        const currentIdx = availableSteps.findIndex((s: any) => s.stepId === selected.stepId);
        if (currentIdx !== -1 && currentIdx < availableSteps.length - 1) {
          const nextStep = availableSteps[currentIdx + 1];
          form.setValue("toStep", nextStep.name);
          form.setValue("toRoutingStepId", nextStep.stepId);
        }

        // Auto-fill quantity with holdingQty if current is 0
        if (form.getValues("quantity") === 0 && selected.holdingQty > 0) {
          form.setValue("quantity", selected.holdingQty);
        }
      }
    }
  }, [watchedFromStep, availableSteps, form]);

  const createMutation = useMutation({
    mutationFn: (data: CreateTransferFormValues) => trpcClient.wipTransfer.create.mutate(data),
    onSuccess: () => {
      toast.success("Tạo phiếu bàn giao bán thành phẩm thành công");
      queryClient.invalidateQueries({ queryKey: ["wip-transfers"] });
      queryClient.invalidateQueries({ queryKey: ["wip-balance-po"] });
      setIsAddDialogOpen(false);
      form.reset();
    },
    onError: (err: any) => {
      toast.error(err.message || "Không thể tạo phiếu bàn giao");
    },
  });

  const onSubmit = (data: CreateTransferFormValues) => {
    const selected = availableSteps.find((s: any) => s.name === data.fromStep || s.stepCode === data.fromStep);
    if (selected && selected.holdingQty > 0 && data.quantity > selected.holdingQty) {
      form.setError("quantity", { message: `Vượt quá tồn BTP khả dụng (${formatNumber(selected.holdingQty)} Tờ)` });
      return;
    }
    createMutation.mutate(data);
  };

  // 2. Server-side list query
  const [activeTabStatus, setActiveTabStatus] = useState<string>("ALL");

  const {
    items,
    isLoading,
    error,
    currentPage,
    pageSize,
    totalItems,
    serverParams,
    onPageChange,
    onPageSizeChange,
    sortField,
    sortOrder,
    handleSort,
    searchValue,
    onSearchChange,
  } = useServerListFilter<any>({
    queryKey: `wip-transfers-${activeTabStatus}`,
    queryFn: (params: any) =>
      trpcClient.wipTransfer.list.query({
        ...params,
        status: activeTabStatus === "ALL" ? undefined : activeTabStatus,
      }) as any,
  });

  // Extra query for summary KPIs
  const { data: kpiData } = useQuery({
    queryKey: ["wip-transfers-kpis"],
    queryFn: () => trpcClient.wipTransfer.list.query({ page: 1, limit: 1 }),
  });

  const kpis = kpiData?.kpis || {
    totalTransfers: 0,
    pendingTransfers: 0,
    receivedTransfers: 0,
    totalRejectedQty: 0,
  };

  const paginatedItems = items || [];
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearAll = () => {
    setFilterValues({});
    setActiveTabStatus("ALL");
  };

  const filteredItems = useMemo(() => {
    return paginatedItems.filter((item: any) => {
      const po = item.productionOrder || {};
      if (filterValues.transferId && !item.transferId?.toLowerCase().includes(filterValues.transferId.toLowerCase())) {
        return false;
      }
      if (filterValues.productionOrderId) {
        const orderId = (po.orderId || item.productionOrderId || "").toLowerCase();
        if (!orderId.includes(filterValues.productionOrderId.toLowerCase())) return false;
      }
      if (filterValues.productName) {
        const prod = (po.productName || item.productName || "").toLowerCase();
        if (!prod.includes(filterValues.productName.toLowerCase())) return false;
      }
      if (filterValues.steps) {
        const stepStr = `${item.fromStep} ${item.toStep}`.toLowerCase();
        if (!stepStr.includes(filterValues.steps.toLowerCase())) return false;
      }
      if (filterValues.status && item.status !== filterValues.status) {
        return false;
      }
      if (filterValues.user) {
        const users = `${item.senderName || ""} ${item.receiverName || ""}`.toLowerCase();
        if (!users.includes(filterValues.user.toLowerCase())) return false;
      }
      return true;
    });
  }, [paginatedItems, filterValues]);

  const rowIds = filteredItems.map((i: any) => i.id || i.transferId);
  const rowSelection = useRowSelection(rowIds);

  const exportData = useMemo(() => {
    return filteredItems.map((item: any) => ({
      ...item,
      productName: item.productionOrder?.productName || item.productName,
    }));
  }, [filteredItems]);

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_ban_giao_btp",
    sheetName: "BanGiaoBTP",
    columns: EXPORT_COLUMNS,
    queryFn: (params) =>
      trpcClient.wipTransfer.list.query({
        ...params,
        status: activeTabStatus === "ALL" ? undefined : activeTabStatus,
      }),
    currentFilters: serverParams,
    transformData: (rows) =>
      (rows || []).map((item: any) => ({
        ...item,
        productName: item.productionOrder?.productName || item.productName,
      })),
    localData: exportData,
    selectedIds: rowSelection.selectedIds,
  });

  return (
    <>
      <ListPageShell
        trashModule="wip-transfer"
        topBanner={
          <CompactKpiRibbon
            items={[
              {
                key: "ALL",
                label: "Tổng Phiếu",
                count: kpis.totalTransfers,
                icon: Layers,
                variant: "default",
                tooltip: "Tất cả phiếu bàn giao",
                clickable: true,
              },
              {
                key: "PENDING",
                label: "Đang Chuyển",
                count: kpis.pendingTransfers,
                icon: Clock,
                variant: "amber",
                tooltip: "Chờ tiếp nhận",
                clickable: true,
              },
              {
                key: "RECEIVED",
                label: "Đã Nhận",
                count: kpis.receivedTransfers,
                icon: CheckCircle2,
                variant: "emerald",
                tooltip: "Đã tiếp nhận thành công",
                clickable: true,
              },
              {
                key: "REJECTED",
                label: "Phế Phẩm BTP",
                count: kpis.totalRejectedQty,
                subLabel: "Tờ",
                icon: AlertTriangle,
                variant: "rose",
                animate: kpis.totalRejectedQty > 0,
                tooltip: "BTP bị từ chối / phế phẩm",
                clickable: true,
              },
            ]}
            activeKey={activeTabStatus}
            onSelect={(key) => setActiveTabStatus((prev) => (prev === key ? "ALL" : (key || "ALL")))}
          />
        }
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              className={emsTabClass(true, { size: "sm" })}
            >
              <Layers className="w-3.5 h-3.5 text-indigo-600" />
              <span>Sổ Giao Nhận BTP</span>
              <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold tabular-nums bg-primary/10 text-primary">
                {totalItems}
              </span>
            </button>
          </div>
        }
        unitLabel="Phiếu"
        availableViews={["table"]}
        activeView="table"
        filterFields={FILTER_FIELDS}
        filterValues={filterValues}
        showFilter={showFilter}
        onToggleFilter={() => setShowFilter((prev) => !prev)}
        onClearAll={handleClearAll}
        actions={
          <ExportImportButtons
            onExport={() => handleExport()}
            isExporting={isExporting}
            onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, "mau_ban_giao_btp")}
          >
            <Button
              size="sm"
              className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 font-bold"
              onClick={() => setIsAddDialogOpen(true)}
            >
              <Plus size={14} className="mr-1.5" /> Tạo Bàn Giao
            </Button>
          </ExportImportButtons>
        }
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-xs caption-bottom apv-compact-table border-collapse">
            <thead className="apv-table-header bg-slate-50 border-b border-border">
              <tr className="whitespace-nowrap">
                <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                <SortableHeader
                  label="Mã Phiếu"
                  field="transferId"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="w-[130px] text-left py-2 px-3 font-bold whitespace-nowrap"
                />
                <SortableHeader
                  label="Lệnh SX"
                  field="productionOrderId"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="w-[120px] text-left py-2 px-2 font-bold whitespace-nowrap"
                />
                <th className="text-left py-2 px-2 font-bold whitespace-nowrap">Sản phẩm bao bì</th>
                <th className="w-[230px] text-left py-2 px-2 font-bold whitespace-nowrap">Công đoạn luân chuyển</th>
                <SortableHeader
                  label="SL Bàn giao"
                  field="quantity"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="w-[110px] text-right py-2 px-2 font-bold whitespace-nowrap"
                />
                <th className="w-[110px] text-right py-2 px-2 font-bold whitespace-nowrap">SL Thực nhận</th>
                <SortableHeader
                  label="Trạng thái"
                  field="status"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="w-[110px] text-center py-2 px-2 font-bold whitespace-nowrap"
                />
                <th className="w-[130px] text-left py-2 px-2 font-bold whitespace-nowrap">Giao / Nhận</th>
                <th className="w-[90px] text-center py-2 px-2 font-bold whitespace-nowrap">Thao tác</th>
              </tr>
              {showFilter && (
                <InlineFilterRow
                  columns={[
                    CHECKBOX_COL_NULL,
                    { key: "transferId" },
                    { key: "productionOrderId" },
                    { key: "productName" },
                    { key: "steps" },
                    null,
                    null,
                    { key: "status" },
                    { key: "user" },
                    null,
                  ]}
                  fields={FILTER_FIELDS}
                  values={filterValues}
                  onChange={handleFilterChange}
                />
              )}
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-indigo-600" />
                    Đang tải dữ liệu bàn giao BTP...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-rose-500">
                    Lỗi khi tải dữ liệu bàn giao BTP
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-slate-400 italic">
                    Chưa có phiếu bàn giao nào trong danh mục này
                  </td>
                </tr>
              ) : (
                filteredItems.map((item: any) => {
                  const po = item.productionOrder || {};
                  return (
                    <DataTableRow
                      key={item.id || item.transferId}
                      id={item.id || item.transferId}
                      rowSelection={rowSelection}
                      onClick={() => {
                        setSelectedTransfer(item);
                        setIsDetailDrawerOpen(true);
                      }}
                      className="hover:bg-slate-50 transition-colors border-b border-border cursor-pointer"
                    >
                      <DataTableRowCheckbox
                        id={item.id || item.transferId}
                        rowSelection={rowSelection}
                      />
                      <td className="px-3 py-2">
                        <span className="font-mono font-bold text-indigo-700">
                          {item.transferId}
                        </span>
                        {item.palletCode && (
                          <div className="text-[10px] font-mono text-slate-500">{item.palletCode}</div>
                        )}
                      </td>
                      <td className="px-2 py-2 font-mono font-semibold text-slate-700">
                        {po.orderId || item.productionOrderId}
                      </td>
                      <td className="px-2 py-2 font-medium text-slate-900 max-w-[220px] truncate">
                        {po.productName || "—"}
                        {po.customer?.name && (
                          <div className="text-[10px] text-slate-500 truncate">{po.customer.name}</div>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <Badge variant="outline" className="font-medium bg-white text-slate-700 px-1.5 py-0 border-slate-300">
                            {item.fromRoutingStep?.name || item.fromStep}
                          </Badge>
                          <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
                          <Badge variant="outline" className="font-bold bg-emerald-50 text-emerald-800 px-1.5 py-0 border-emerald-300">
                            {item.toRoutingStep?.name || item.toStep}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-bold text-slate-900">
                        {formatNumber(item.quantity)}
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-bold">
                        {item.status === "RECEIVED" ? (
                          <span className="text-emerald-700">{formatNumber(item.receivedQty)}</span>
                        ) : (
                          <span className="text-slate-400 font-normal">—</span>
                        )}
                        {item.rejectedQty > 0 && (
                          <div className="text-[10px] text-rose-600 font-medium">
                            -{formatNumber(item.rejectedQty)} hỏng
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <Badge
                          className={cn(
                            "text-[10px] px-2 py-0.5 border font-semibold",
                            item.status === "RECEIVED"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                              : item.status === "REJECTED"
                              ? "bg-rose-50 text-rose-700 border-rose-300"
                              : "bg-amber-50 text-amber-700 border-amber-300"
                          )}
                        >
                          {item.status === "RECEIVED" ? "Đã nhận" : item.status === "REJECTED" ? "Từ chối" : "Đang chuyển"}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-[11px] text-slate-600">
                        <div>Giao: <span className="font-medium text-slate-800">{item.senderName || "—"}</span></div>
                        {item.receiverName && (
                          <div>Nhận: <span className="font-medium text-emerald-800">{item.receiverName}</span></div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          {item.status === "PENDING" && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold"
                              title="Tiếp nhận BTP"
                              onClick={() => setReceiveModalState({ open: true, transfer: item })}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Nhận
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-slate-600 hover:text-indigo-600"
                            title="Xem chi tiết phiếu bàn giao"
                            onClick={() => {
                              setSelectedTransfer(item);
                              setIsDetailDrawerOpen(true);
                            }}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-slate-600 hover:text-indigo-600"
                            title="In thẻ bàn giao"
                            onClick={() => {
                              setPrintData([item]);
                              setIsPrintDialogOpen(true);
                            }}
                          >
                            <Printer className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </DataTableRow>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </ListPageShell>

      {/* Modal Tạo Phiếu Bàn Giao */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Tạo Phiếu Bàn Giao Bán Thành Phẩm</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Điều chuyển BTP giữa các công đoạn theo đúng tuần tự quy trình sản xuất (INV-08).
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3.5 pt-2">
              <FormField
                control={form.control}
                name="productionOrderId"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-xs font-semibold text-slate-700">
                      Lệnh Sản Xuất <span className="text-rose-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <ComboboxFK
                        value={field.value}
                        onChange={field.onChange}
                        options={lsxOptions}
                        placeholder="Chọn Lệnh SX đang sản xuất..."
                        canAdd={false}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="fromStep"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-xs font-semibold text-slate-700">
                        Công đoạn xuất <span className="text-rose-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxFK
                          value={field.value}
                          onChange={field.onChange}
                          options={fromStepOptions}
                          placeholder="Chọn công đoạn giao..."
                          canAdd={false}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="toStep"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-xs font-semibold text-slate-700">
                        Công đoạn nhận <span className="text-rose-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <ComboboxFK
                          value={field.value}
                          onChange={field.onChange}
                          options={toStepOptions}
                          placeholder="Chọn công đoạn đến..."
                          canAdd={false}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="quantity"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-xs font-semibold text-slate-700">
                        Số lượng bàn giao (Tờ) <span className="text-rose-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <NumericInput
                          value={field.value}
                          onChange={field.onChange}
                          className="font-mono font-bold text-slate-900"
                          min={1}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="palletCode"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-xs font-semibold text-slate-700">
                        Mã Kiện / Pallet (Tùy chọn)
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="VD: PLT-01/04"
                          value={field.value || ""}
                          onChange={field.onChange}
                          className="font-mono text-xs"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="senderName"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-xs font-semibold text-slate-700">
                        Thợ máy giao ca
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Tên thợ máy giao..."
                          value={field.value || ""}
                          onChange={field.onChange}
                          className="text-xs"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-xs font-semibold text-slate-700">
                        Ghi chú
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ghi chú thêm..."
                          value={field.value || ""}
                          onChange={field.onChange}
                          className="text-xs"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter className="pt-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddDialogOpen(false)}
                  disabled={createMutation.isPending}
                >
                  Hủy
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700 font-bold"
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      Đang xử lý...
                    </>
                  ) : (
                    "Tạo phiếu bàn giao"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Modal Tiếp Nhận BTP */}
      <ReceiveWipModal
        open={receiveModalState.open}
        onOpenChange={(open) => setReceiveModalState((prev) => ({ ...prev, open }))}
        transfer={receiveModalState.transfer}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["wip-transfers"] });
          queryClient.invalidateQueries({ queryKey: ["wip-balance-po"] });
          queryClient.invalidateQueries({ queryKey: ["wip-transfers-kpis"] });
        }}
      />

      {/* Modal In Thẻ Bàn Giao */}
      <PrintDialog
        open={isPrintDialogOpen}
        onOpenChange={setIsPrintDialogOpen}
        title="Thẻ Bàn Giao Bán Thành Phẩm"
      >
        {printData.length > 0 && (
          <WipTransferPrintTemplate transfer={printData[0]} />
        )}
      </PrintDialog>

      {/* Side Drawer Chi Tiết BTP */}
      <WipTransferDetailDrawer
        open={isDetailDrawerOpen}
        onOpenChange={setIsDetailDrawerOpen}
        transfer={selectedTransfer}
        onPrint={(tr) => {
          setPrintData([tr]);
          setIsPrintDialogOpen(true);
        }}
        onCancel={(tr) => {
          if (window.confirm("Bạn có chắc chắn muốn hủy phiếu luân chuyển BTP này không?")) {
            toast.promise(trpcClient.wipTransfer.cancel.mutate({ id: tr.id }), {
              loading: "Đang hủy phiếu...",
              success: () => {
                queryClient.invalidateQueries({ queryKey: ["wip-transfers"] });
                setIsDetailDrawerOpen(false);
                return "Hủy phiếu thành công";
              },
              error: (err) => err.message || "Lỗi khi hủy phiếu",
            });
          }
        }}
        onEdit={(tr) => {
          // Future implement: open Edit modal
          toast.warning("Tính năng sửa phiếu đang được cập nhật");
        }}
      />
    </>
  );
}
